import BackToCPQButton from "../../Components/backToCPQButton";
import PreviousPageButtonCPQ from "../../Components/PreviousPageButtonCPQ";

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Button,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Grid,
    Snackbar,
    Alert,
    CircularProgress,
    InputAdornment,
    Tooltip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    FilterAlt as FilterIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";
import apiClient from "../../Helpers/apiClient";

function TankPriceTable() {
    const [tanks, setTanks] = useState([]);
    const [filteredTanks, setFilteredTanks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedTank, setSelectedTank] = useState(null);
    const [formData, setFormData] = useState({
        id: 0,
        diameter: 1000,
        depth: 1000,
        price: 0,
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    // Sorting states
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");
    // Filtering states
    const [filterText, setFilterText] = useState("");
    const [filterField, setFilterField] = useState("all");

    const navigate = useNavigate();

    useEffect(() => {
        fetchTanks();
    }, []);

    // Apply filters whenever tanks or filter settings change
    useEffect(() => {
        applyFilters();
    }, [tanks, filterText, filterField]);

    const fetchTanks = async () => {
        setLoading(true);
        try {
            console.log("Fetching tanks...");

            const response = await apiClient.get("/api/PumpStationPriceTables/TankPrices");

            console.log("Response received successfully!");
            console.log("Data type:", typeof response.data);
            console.log("Is array:", Array.isArray(response.data));

            if (Array.isArray(response.data)) {
                setTanks(response.data);
                console.log(`Successfully loaded ${response.data.length} tanks`);
            } else {
                console.warn("Response is not an array:", response.data);
                setTanks([]);
            }

            setError(null);
        } catch (err) {
            console.error("Error fetching tanks:", err);
            setError(`Failed to load data: ${err.message}`);
            setTanks([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (tank = null) => {
        if (tank) {
            setFormData({
                id: tank.id || tank.Id || 0,
                diameter: tank.diameter || tank.Diameter || 1000,
                depth: tank.depth || tank.Depth || 1000,
                price: tank.price || tank.Price || 0,
            });
            setSelectedTank(tank);
        } else {
            setFormData({
                id: 0,
                diameter: 1000,
                depth: 1000,
                price: 0,
            });
            setSelectedTank(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleOpenDeleteDialog = (tank) => {
        setSelectedTank(tank);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedTank(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]:
                name === "price" || name === "diameter" || name === "depth" ? parseFloat(value) || 0 : value,
        });
    };

    const handleSubmit = async () => {
        try {
            // Validate diameter and depth ranges (1000-10000)
            if (formData.diameter < 1000 || formData.diameter > 10000) {
                setSnackbar({
                    open: true,
                    message: "Diameter must be between 1000 and 10000",
                    severity: "error",
                });
                return;
            }

            if (formData.depth < 1000 || formData.depth > 10000) {
                setSnackbar({
                    open: true,
                    message: "Depth must be between 1000 and 10000",
                    severity: "error",
                });
                return;
            }

            // Convert camelCase to PascalCase to match C# model property names
            const payload = {
                Id: formData.id,
                Diameter: formData.diameter,
                Depth: formData.depth,
                Price: formData.price,
                Quotations: [],
            };

            let response;
            if (selectedTank) {
                // Update existing record
                response = await apiClient.put(
                    `/api/PumpStationPriceTables/TankPrices/${payload.Id}`,
                    payload
                );
                setSnackbar({
                    open: true,
                    message: "Tank updated successfully!",
                    severity: "success",
                });
            } else {
                // Create new record
                response = await apiClient.post("/api/PumpStationPriceTables/TankPrices", payload);
                setSnackbar({
                    open: true,
                    message: "Tank created successfully!",
                    severity: "success",
                });
            }

            handleCloseDialog();
            fetchTanks();
        } catch (err) {
            console.error("Error saving tank:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to save. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await apiClient.delete(
                `/api/PumpStationPriceTables/TankPrices/${selectedTank.id || selectedTank.Id}`
            );
            setSnackbar({
                open: true,
                message: "Tank deleted successfully!",
                severity: "success",
            });
            handleCloseDeleteDialog();
            fetchTanks();
        } catch (err) {
            console.error("Error deleting tank:", err);
            setSnackbar({
                open: true,
                message: `Error: ${err.response?.data?.message || "Failed to delete. Please try again."}`,
                severity: "error",
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Sorting functionality
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const getSortedData = (data) => {
        if (!data.length) return [];

        return [...data].sort((a, b) => {
            // Handle the ID/modelNo column specially
            if (orderBy === "modelNo") {
                const idA = parseInt(a.id || a.Id || 0);
                const idB = parseInt(b.id || b.Id || 0);
                return order === "asc" ? idA - idB : idB - idA;
            }

            // For any other column, get the property with both camelCase and PascalCase checks
            let valueA, valueB;

            // Try camelCase first, then PascalCase if not found
            if (orderBy === "diameter") {
                valueA = a.diameter !== undefined ? a.diameter : a.Diameter;
                valueB = b.diameter !== undefined ? b.diameter : b.Diameter;

                // Convert to numbers for comparison
                valueA = parseInt(valueA) || 0;
                valueB = parseInt(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "depth") {
                valueA = a.depth !== undefined ? a.depth : a.Depth;
                valueB = b.depth !== undefined ? b.depth : b.Depth;

                // Convert to numbers for comparison
                valueA = parseInt(valueA) || 0;
                valueB = parseInt(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            } else if (orderBy === "price") {
                valueA = a.price !== undefined ? a.price : a.Price;
                valueB = b.price !== undefined ? b.price : b.Price;

                // Convert to numbers for price comparison
                valueA = parseFloat(valueA) || 0;
                valueB = parseFloat(valueB) || 0;
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            // For non-numeric values, convert to strings and compare
            if (typeof valueA !== "number") {
                valueA = String(valueA || "").toLowerCase();
                valueB = String(valueB || "").toLowerCase();
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            // For numeric values
            return order === "asc" ? valueA - valueB : valueB - valueA;
        });
    };

    // Filtering functionality
    const handleFilterChange = (event) => {
        setFilterText(event.target.value);
    };

    const handleFilterFieldChange = (field) => {
        setFilterField(field);
    };

    const clearFilter = () => {
        setFilterText("");
        setFilterField("all");
    };

    const applyFilters = () => {
        if (!filterText.trim()) {
            setFilteredTanks(tanks);
            return;
        }

        const searchTerm = filterText.toLowerCase().trim();

        const filtered = tanks.filter((tank) => {
            // Format model number for filtering (TK-001, TK-002, etc.)
            const modelNo = `TK-${(tank.id || tank.Id || 0).toString().padStart(3, "0")}`;

            // Handle both camelCase and PascalCase property names from API
            const diameter = tank.diameter || tank.Diameter || 0;
            const depth = tank.depth || tank.Depth || 0;
            const price = tank.price || tank.Price || 0;

            if (filterField === "all") {
                return (
                    modelNo.toLowerCase().includes(searchTerm) ||
                    String(tank.id || tank.Id || "").includes(searchTerm) ||
                    String(diameter).includes(searchTerm) ||
                    String(depth).includes(searchTerm) ||
                    String(price).includes(searchTerm)
                );
            }

            // Filter by specific field
            if (filterField === "modelNo") return modelNo.toLowerCase().includes(searchTerm);
            if (filterField === "id") return String(tank.id || tank.Id || "").includes(searchTerm);
            if (filterField === "diameter") return String(diameter).includes(searchTerm);
            if (filterField === "depth") return String(depth).includes(searchTerm);
            if (filterField === "price") return String(price).includes(searchTerm);

            return false;
        });

        setFilteredTanks(filtered);
    };

    // Prepare data for rendering
    const displayData = getSortedData(filteredTanks);

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                padding: 4,
                paddingTop: 2,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            <Typography variant="h4" gutterBottom align="center">
                Tank Price Table
            </Typography>

            <Paper
                sx={{
                    width: "100%",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        alignItems: "center",
                    }}
                >
                    {/* Filter */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "60%",
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Search..."
                            size="small"
                            fullWidth
                            value={filterText}
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: filterText ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={clearFilter} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Tooltip title="Filter by field">
                            <Box>
                                <IconButton
                                    color={filterField !== "all" ? "primary" : "default"}
                                    aria-label="Filter options"
                                    onClick={(e) => {
                                        e.currentTarget.nextElementSibling.style.display =
                                            e.currentTarget.nextElementSibling.style.display === "none"
                                                ? "flex"
                                                : "none";
                                    }}
                                >
                                    <FilterIcon />
                                </IconButton>
                                <Box
                                    sx={{
                                        position: "absolute",
                                        zIndex: 10,
                                        bgcolor: "background.paper",
                                        boxShadow: 3,
                                        borderRadius: 1,
                                        p: 1,
                                        display: "none",
                                        flexDirection: "column",
                                        gap: 0.5,
                                    }}
                                >
                                    {[
                                        { id: "all", label: "All Fields" },
                                        { id: "modelNo", label: "ID" },
                                        {
                                            id: "diameter",
                                            label: "Diameter (mm)",
                                        },
                                        { id: "depth", label: "Depth (mm)" },
                                        { id: "price", label: "Price" },
                                    ].map((option) => (
                                        <Button
                                            key={option.id}
                                            size="small"
                                            variant={filterField === option.id ? "contained" : "text"}
                                            onClick={() => {
                                                handleFilterFieldChange(option.id);
                                                // Hide the filter dropdown
                                                document.querySelector(
                                                    '[aria-label="Filter options"]'
                                                ).nextElementSibling.style.display = "none";
                                            }}
                                        >
                                            {option.label}
                                        </Button>
                                    ))}
                                </Box>
                            </Box>
                        </Tooltip>
                        {filterField !== "all" && (
                            <Typography variant="caption" sx={{ ml: 1 }}>
                                Filtering by:{" "}
                                {filterField === "modelNo"
                                    ? "ID"
                                    : filterField === "id"
                                    ? "ID"
                                    : filterField === "diameter"
                                    ? "Diameter"
                                    : filterField === "depth"
                                    ? "Depth"
                                    : filterField === "price"
                                    ? "Price"
                                    : ""}
                            </Typography>
                        )}
                    </Box>

                    {/* Add button */}
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Tank
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer
                            sx={{
                                flex: 1,
                                overflow: "auto",
                            }}
                        >
                            <Table stickyHeader aria-label="tank price table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "modelNo"}
                                                direction={orderBy === "modelNo" ? order : "asc"}
                                                onClick={() => handleRequestSort("modelNo")}
                                            >
                                                <strong>ID</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "diameter"}
                                                direction={orderBy === "diameter" ? order : "asc"}
                                                onClick={() => handleRequestSort("diameter")}
                                            >
                                                <strong>Diameter (mm)</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "depth"}
                                                direction={orderBy === "depth" ? order : "asc"}
                                                onClick={() => handleRequestSort("depth")}
                                            >
                                                <strong>Depth (mm)</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === "price"}
                                                direction={orderBy === "price" ? order : "asc"}
                                                onClick={() => handleRequestSort("price")}
                                            >
                                                <strong>Price</strong>
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Actions</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                {filterText
                                                    ? "No matches found. Try a different search."
                                                    : "No data available. Add your first tank."}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayData.map((tank) => (
                                            <TableRow key={tank.id || tank.Id} hover>
                                                <TableCell>
                                                    TK-
                                                    {(tank.id || tank.Id).toString().padStart(3, "0")}
                                                </TableCell>
                                                <TableCell>{tank.diameter || tank.Diameter} mm</TableCell>
                                                <TableCell>{tank.depth || tank.Depth} mm</TableCell>
                                                <TableCell>
                                                    $
                                                    {typeof (tank.price || tank.Price) === "number"
                                                        ? (tank.price || tank.Price).toFixed(2)
                                                        : "0.00"}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(tank)}
                                                        aria-label="edit"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleOpenDeleteDialog(tank)}
                                                        aria-label="delete"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {filterText
                                    ? `Showing ${displayData.length} of ${tanks.length} items`
                                    : `Total: ${tanks.length} items`}
                            </Typography>
                            {filterText && (
                                <Button size="small" startIcon={<ClearIcon />} onClick={clearFilter}>
                                    Clear Filter
                                </Button>
                            )}
                        </Box>
                    </>
                )}
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <BackToCPQButton />
                <PreviousPageButtonCPQ />
            </Box>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedTank ? "Edit Tank" : "Add New Tank"}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Diameter (mm)"
                                    name="diameter"
                                    type="number"
                                    inputProps={{
                                        min: "1000",
                                        max: "10000",
                                        step: "10",
                                    }}
                                    value={formData.diameter}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="Range: 1000-10000 mm"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Depth (mm)"
                                    name="depth"
                                    type="number"
                                    inputProps={{
                                        min: "1000",
                                        max: "10000",
                                        step: "10",
                                    }}
                                    value={formData.depth}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                    helperText="Range: 1000-10000 mm"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Price"
                                    name="price"
                                    type="number"
                                    inputProps={{ step: "0.01", min: "0" }}
                                    value={formData.price}
                                    onChange={handleFormChange}
                                    required
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {selectedTank ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this tank? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default TankPriceTable;
